import React, { useState, useEffect, useCallback } from 'react';
import styles from './ItemManager.module.css';
import { PrimeArtifactState } from '../../types/primeModel';
import { DemandType, DemandFlowType, ArtifactStateTransitionType, ProcessType } from '../../types/enums';
import { UUID } from '../../types/common';

export interface Item {
  id: UUID;
  name: string;
  description?: string | null | undefined;
  prime_artifact_ref?: string;
  prime_process_ref?: string;
  from_state_ref?: string;
  to_state_ref?: string;
  default_transition_type?: ArtifactStateTransitionType;
  transition_parameters?: Record<string, unknown>;
  validation_rules?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string | null | undefined;
  demand_type?: DemandType;
  demand_flow_type?: DemandFlowType;
  process_type?: ProcessType;
  nested_state_machine_ref?: string;
  improvement_ref?: string | null | undefined;
  improvement_parameters?: Record<string, unknown>;
  process_parameters?: Record<string, unknown>;
  demand_calculation_rules?: Record<string, unknown>;
  [key: string]: unknown; // For additional properties
}

interface ItemManagerProps {
  title: string;
  items: Item[];
  selectedItem?: Item | null;
  onSelect: (item: Item) => void;
  onUpdate?: (item: Item) => void;
  onDelete?: (item: Item) => void;
  onAdd?: (item: Partial<Item>) => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  isEditing?: boolean;
  renderDetails?: (item: Item, isEditing: boolean, onEdit: (item: Item) => void) => React.ReactNode;
  states?: PrimeArtifactState[];
  additionalActions?: React.ReactNode;
}

export const ItemManager: React.FC<ItemManagerProps> = ({
  title,
  items,
  selectedItem,
  onSelect,
  onUpdate,
  onDelete,
  onAdd,
  onEdit,
  onCancel,
  onSave,
  isEditing,
  renderDetails,
  states = [],
  additionalActions,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState<Partial<Item>>({});

  useEffect(() => {
    console.log('[ItemManager] Mounted with props:', {
      title,
      itemsCount: items.length,
      hasOnAdd: !!onAdd,
      hasSelectedItem: !!selectedItem,
      selectedItemId: selectedItem?.id,
      isEditing,
    });
  }, [title, items.length, onAdd, selectedItem, isEditing]);

  useEffect(() => {
    console.log('[ItemManager] Selection state changed:', {
      selectedItemId: selectedItem?.id,
      selectedItemName: selectedItem?.name,
      isEditing,
    });
  }, [selectedItem, isEditing]);

  const handleAdd = () => {
    console.log('[ItemManager] Add button clicked');
    setIsAdding(true);
    setNewItem({
      name: '',
      description: '',
      state_type: 'NORMAL',
      demand_type: DemandType.TARGET,
      demand_flow_type: DemandFlowType.DRUM,
      is_required: false,
      is_initial: false,
      is_final: false,
      is_improvement: false,
    });

    if (onAdd) {
      onAdd(newItem);
    }
  };

  const handleSave = () => {
    console.log('[ItemManager] Save button clicked for new item:', newItem);
    if (onAdd) {
      onAdd(newItem);
    }
    if (onSave) {
      onSave();
    }
    setIsAdding(false);
    setNewItem({});
  };

  const handleCancel = () => {
    console.log('[ItemManager] Cancel button clicked');
    setIsAdding(false);
    setNewItem({});
    if (onCancel) {
      onCancel();
    }
  };

  const handleDelete = (item: Item) => {
    console.log('[ItemManager] Delete button clicked for item:', item.id);
    if (onDelete) {
      onDelete(item);
    }
  };

  const handleItemSelect = useCallback(
    (item: Item) => {
      console.log('[ItemManager] Item selected:', {
        id: item.id,
        name: item.name,
        isSelected: selectedItem?.id === item.id,
      });
      onSelect(item);
    },
    [onSelect, selectedItem?.id]
  );

  const renderItem = useCallback(
    (item: Item) => {
      const isTransition = item.from_state_ref !== undefined && item.to_state_ref !== undefined;
      const isSelected = selectedItem?.id === item.id;

      console.log('[ItemManager] Rendering item:', {
        id: item.id,
        name: item.name,
        isSelected,
        isTransition,
        selectedItemId: selectedItem?.id,
      });

      if (isTransition) {
        const fromState = states.find((s: PrimeArtifactState) => s.id === item.from_state_ref);
        const toState = states.find((s: PrimeArtifactState) => s.id === item.to_state_ref);

        return (
          <div
            key={item.id}
            className={`${styles.itemListItem} ${isSelected ? styles.selected : ''}`}
            onClick={() => handleItemSelect(item)}
          >
            <div className={styles.transitionListItem}>
              <span className={styles.transitionStates}>
                {fromState?.name || 'Unknown'} → {toState?.name || 'Unknown'}
              </span>
              <span className={styles.transitionProcess}>{item.prime_process_ref}</span>
            </div>
          </div>
        );
      }
      return (
        <div
          key={item.id}
          className={`${styles.itemListItem} ${isSelected ? styles.selected : ''}`}
          onClick={() => handleItemSelect(item)}
        >
          {item.name}
        </div>
      );
    },
    [handleItemSelect, selectedItem?.id, states]
  );

  return (
    <div className={styles.itemManager}>
      <div className={styles.itemListSection}>
        <div className={styles.sectionHeader}>
          <h2>{title}</h2>
          <div className={styles.headerActions}>
            {additionalActions}
            {onAdd && (
              <button onClick={handleAdd} className={styles.addButton}>
                Add New
              </button>
            )}
          </div>
        </div>
        <div className={styles.itemList}>{items.map(renderItem)}</div>
      </div>
      <div className={styles.itemDetailsSection}>
        {isAdding ? (
          <div className={styles.itemDetails}>
            <h3>New {title}</h3>
            {renderDetails && renderDetails(newItem as Item, true, (item) => setNewItem(item))}
            <div className={styles.actionButtons}>
              <button onClick={handleSave} className={styles.saveButton}>
                Save
              </button>
              <button onClick={handleCancel} className={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        ) : selectedItem ? (
          <div className={styles.itemDetails}>
            <div className={styles.detailsHeader}>
              <h3>{title} Details</h3>
              {!isEditing && onEdit && (
                <button
                  onClick={() => {
                    console.log('[ItemManager] Edit button clicked for item:', selectedItem.id);
                    onEdit();
                  }}
                  className={styles.editButton}
                >
                  Edit
                </button>
              )}
            </div>
            {renderDetails &&
              renderDetails(selectedItem, isEditing ?? false, onUpdate || (() => {}))}
            {isEditing ? (
              <div className={styles.actionButtons}>
                {onSave && (
                  <button onClick={onSave} className={styles.saveButton}>
                    Save
                  </button>
                )}
                {onCancel && (
                  <button onClick={onCancel} className={styles.cancelButton}>
                    Cancel
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.actionButtons}>
                {onDelete && (
                  <button onClick={() => handleDelete(selectedItem)} className={styles.deleteButton}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.noSelection}>Select an item to view its details</div>
        )}
      </div>
    </div>
  );
};
